/**
 * Ordinals Wallet datafeed for TradingView Advanced Charts.
 *
 * One datafeed backs every chart on the site. Before this, collections, the
 * markets pages, alkanes, and sales-volume each had their own chart component
 * with its own fetching, bucketing, theming and range handling — five
 * implementations that disagreed about what a candle was and drifted apart
 * whenever one was fixed. Anything that plugs a symbol into this feed now
 * renders identically to everything else.
 *
 * ## Degraded mode
 *
 * The real series comes from `/collection/:slug/ohlcv`, which serves genuine
 * OHLC. Where that endpoint is not deployed yet, the feed falls back to the
 * legacy `/collection/:slug/chart`, which only carries a VWAP line. There is
 * no honest way to turn a line into candles, so degraded bars are emitted flat
 * (open = high = low = close) and `onDegraded` fires so the UI can say the
 * chart is a line because that is all the data supports — rather than drawing
 * candle bodies that were never measured.
 */

import { API_URL } from '@/utils/constants'
import { fetchCandleSnapshot } from '@/utils/hyperliquid'
import { chartLog } from './debug'
import { learnUnit } from './units'
import {
	parseSymbol,
	priceScaleFor,
	priceScaleForPrice,
	resolutionSeconds,
	resolutionToInterval,
	SUPPORTED_RESOLUTIONS,
	type ChartKind,
	type Denom
} from './symbol'

/** Chart API base. Mirrors the legacy hook so a local backend can be pointed
 * at just the chart endpoints without redirecting the whole app. */
const CHART_BASE =
	process.env.NEXT_PUBLIC_CHART_API_URL || process.env.NEXT_PUBLIC_API_URL || API_URL

/** A bar in TradingView's shape: milliseconds, and volume optional. */
export interface Bar {
	time: number
	open: number
	high: number
	low: number
	close: number
	volume?: number
}

/** A candle exactly as the API serves it: unix seconds, and richer. */
export interface OhlcvBar {
	time: number
	open: number
	high: number
	low: number
	close: number
	median: number
	volume: number
	trades: number
	synthetic: boolean
}

export interface DatafeedOptions {
	/** Fires the first time a symbol falls back to the legacy VWAP endpoint. */
	onDegraded?: (symbol: string) => void
	/** The server's own check of the series it served; empty when clean. */
	onInvariants?: (symbol: string, violations: ChartViolation[]) => void
	/** Every sale the fence kept in the window, at the price it cleared. */
	onPrints?: (symbol: string, prints: ChartOutlier[]) => void
	/**
	 * A circulating supply the page already knows, in whole tokens, for a
	 * market-cap series the server may not be able to price on its own. Read
	 * per request so a value that arrives after the widget is built still
	 * counts.
	 */
	supplyHint?: () => number | undefined
	/**
	 * Produce a supply the page does not have yet, called only after the
	 * server has refused a market-cap series for want of one. The server
	 * remembers what it is handed, so this runs once per token ever, not
	 * once per viewer -- and the chart mounts without waiting for it.
	 */
	resolveSupplyHint?: () => Promise<number | undefined>
	/**
	 * A price typical of the series in a denomination, if the page already
	 * knows one -- a live mark, a floor. Read when a symbol is resolved so
	 * the price scale can be sized to the series (see `priceScaleForPrice`)
	 * without a round trip. Missing, the feed reads one bar to find out,
	 * and remembers the answer in local storage for the next visit.
	 */
	typicalPrice?: (denom: Denom) => number | undefined
	/**
	 * Fires when a denomination cannot be served for this asset at all.
	 *
	 * Distinct from "no data": a TAP token has no circulating supply on the
	 * API, so a market cap for it does not exist rather than being temporarily
	 * missing. An empty chart with no explanation reads as broken.
	 */
	onDenomUnavailable?: (symbol: string, reason: string) => void
	/**
	 * Trades the server fenced out of the candles.
	 *
	 * Reported rather than dropped: a grail sale is real, it simply is not the
	 * collection's price. Plotted apart from the bars so the price scale never
	 * has to stretch to contain them.
	 */
	onOutliers?: (symbol: string, outliers: ChartOutlier[]) => void
	/**
	 * The rolling fair-value band for the fetched window.
	 *
	 * Delivered through the same response as the bars rather than fetched
	 * separately: the band is computed from the same tape, in the same
	 * denomination, over the same buckets, and a second request could only
	 * disagree with the first.
	 */
	onTrend?: (symbol: string, trend: ChartTrendPoint[]) => void
	/** Fires with the newest bar so a header can show a live price. */
	onTick?: (symbol: string, bar: Bar) => void
	/** Fires with every batch of historical bars, so a parent can compute a
	 * range change without asking the widget to export its data. */
	onBars?: (symbol: string, bars: Bar[]) => void
	/** Social markers to hang on the time axis, keyed by symbol. */
	getMarks?: (symbol: string, from: number, to: number) => Promise<ChartMark[]>
	/**
	 * Bars supplied by the caller instead of fetched.
	 *
	 * The alkanes terminal already holds its series in a store — fetched once
	 * by the page's own subscription, with its own USD-vs-pool fallback and
	 * market-cap scaling. Routing it through this datafeed's fetchers would
	 * mean fetching the same data twice and re-deriving semantics the page
	 * already owns. So the page hands the bars over and this feed just draws
	 * them.
	 *
	 * Returning an empty array means "the caller has nothing yet", which is
	 * reported as no data rather than as an error.
	 */
	staticBars?: () => OhlcvBar[]
	/**
	 * Human name for the legend.
	 *
	 * Without it the legend prints the raw id — `2:0` for an alkane, a slug for
	 * a collection — which is an address, not a name. DexScreener puts the
	 * ticker there for the same reason: the legend is the one label that stays
	 * on screen while you read the chart.
	 */
	displayName?: string
}

/** A real sale the server's fence kept out of the candles. */
export interface ChartOutlier {
	/** Unix seconds. */
	time: number
	/** Price in the chart's current denomination. */
	price: number
	/** `<txid>i<n>` of the item that changed hands, when the server knows. */
	inscription?: string
	/** Lot size in token units for a fungible sale; absent for an NFT. */
	amount?: number
	/** What it sold for, in sats, whatever the axis shows. */
	price_sats?: number
}

/**
 * One bucket of the rolling fair-value band.
 *
 * The candles answer "what printed". This answers "what is one of these
 * worth", which for a collection of non-identical items is a different and
 * harder question, and the one a holder actually has.
 *
 * Every field is already in the chart's current denomination — the server
 * scales the band through the same BTC series as the candles, so a line and a
 * bar in one chart are never in two units.
 */
export interface ChartViolation {
	rule: string
	at: number
	detail: string
}

export interface ChartTrendPoint {
	/** Unix seconds. */
	time: number
	/** Low decile: the floor proxy. */
	p10: number
	/** Median: the market price. */
	p50: number
	/** High decile: where the strong pieces trade. */
	p90: number
	/** The fair line: floor-anchored, with a share of the upside. */
	fair: number
	/** Trades behind this point. Low counts mean a soft line. */
	samples: number
}

export interface ChartMark {
	id: string | number
	time: number
	label: string
	labelFontColor: string
	color: { border: string; background: string }
	minSize: number
	text: string
}

/**
 * Most buckets the OHLCV endpoint will return in one request.
 *
 * Mirrors `Interval::MAX_BUCKETS` in the API. Asking for more is not a soft
 * failure there — it answers 400, which this feed reads as "no data" and the
 * chart renders empty. TradingView asks for a wide range whenever a symbol
 * changes, so without clamping, any fine resolution over a long window simply
 * showed nothing.
 */
const MAX_BUCKETS_PER_REQUEST = 5000

/**
 * Narrow a requested window to what the server will serve.
 *
 * Clamping the START rather than the end keeps the most RECENT data, which is
 * what a chart opens on; dropping the far end of history is invisible until
 * the user scrolls there, and scrolling issues its own request.
 */
function clampWindow(from: number, to: number, resolution: string): number {
	const span = MAX_BUCKETS_PER_REQUEST * resolutionSeconds(resolution)
	return Math.max(from, to - span)
}

/**
 * How far back to widen a first request that came back empty.
 *
 * A year covers any collection whose trading has merely paused; one that has
 * been silent longer than that has no chart worth landing on anyway.
 */
const WIDEN_LOOKBACK_SECS = 365 * 24 * 60 * 60

/** How often an in-progress bar is re-fetched. */
const REALTIME_POLL_MS = 15_000

/**
 * A bar's shape must satisfy `low <= min(open, close)` and
 * `high >= max(open, close)` or TradingView renders it wrong. The API
 * guarantees this, but the legacy fallback and any future source may not, so
 * it is enforced here — cheaply, and at the only point every bar passes.
 */
function coerceBar(b: OhlcvBar): Bar {
	const open = Number(b.open)
	const close = Number(b.close)
	return {
		// TradingView wants milliseconds; the API speaks unix seconds.
		time: b.time * 1000,
		open,
		high: Math.max(Number(b.high), open, close),
		low: Math.min(Number(b.low), open, close),
		close,
		volume: Number(b.volume) || 0
	}
}

async function fetchOhlcv(
	id: string,
	denom: Denom,
	interval: string,
	from: number,
	to: number
): Promise<OhlcvBar[] | null> {
	const url = `${CHART_BASE}/collection/${encodeURIComponent(
		id
	)}/ohlcv?interval=${interval}&denom=${denom}&start=${from}&end=${to}`
	const res = await fetch(url)
	if (!res.ok) {
		return null
	}
	const json = await res.json()
	return Array.isArray(json?.candles) ? json.candles : null
}

/**
 * Widest window the legacy endpoint will accept per interval, mirroring
 * `Interval::max_window_secs` in the API. Asking for more is not a soft
 * failure there — it returns an error object, which is how a widened request
 * silently produced an empty chart before this was clamped.
 */
const LEGACY_MAX_WINDOW: Record<string, number> = {
	'1h': 90 * 86400,
	'1d': 5 * 365 * 86400
}

/**
 * `fetchOhlcv`, but keeping the server's explanation when it declines.
 *
 * The endpoint answers a denomination it cannot serve with a message saying
 * why — no circulating supply, no BTC series for the window. That sentence is
 * worth more to a user than an empty chart, so it is carried back rather than
 * collapsed into `null`.
 */
async function fetchOhlcvDetailed(
	id: string,
	denom: Denom,
	interval: string,
	from: number,
	to: number,
	supplyHint?: number
): Promise<{
	bars: OhlcvBar[] | null
	reason?: string
	outliers?: ChartOutlier[]
	prints?: ChartOutlier[]
	trend?: ChartTrendPoint[]
	violations?: ChartViolation[]
}> {
	const hint =
		denom === 'mcap' && supplyHint && Number.isFinite(supplyHint) && supplyHint > 0
			? `&supply=${supplyHint}`
			: ''
	const url = `${CHART_BASE}/collection/${encodeURIComponent(
		id
	)}/ohlcv?interval=${interval}&denom=${denom}&start=${from}&end=${to}${hint}`
	const res = await fetch(url)
	if (!res.ok) {
		let reason: string | undefined
		try {
			const body = await res.json()
			reason = typeof body?.message === 'string' ? body.message : undefined
		} catch {
			// Non-JSON error body; the status alone will have to do.
		}
		return { bars: null, reason }
	}
	const json = await res.json()
	// The server checks its own series before serving it and says so. A
	// violation here is a bug on the server; surface it where the chart's
	// other debug output goes rather than let it pass as "looks a bit off".
	const violations = json?.invariants?.violations
	if (Array.isArray(violations) && violations.length > 0) {
		chartLog('feed', 'server invariants broken', {
			url: url.replace(CHART_BASE, ''),
			violations
		})
	}
	return {
		bars: Array.isArray(json?.candles) ? json.candles : null,
		outliers: Array.isArray(json?.outliers) ? json.outliers : undefined,
		prints: Array.isArray(json?.prints) ? json.prints : undefined,
		trend: Array.isArray(json?.trend) ? json.trend : undefined,
		violations: Array.isArray(violations) ? violations : []
	}
}

/**
 * Legacy VWAP endpoint, shaped into flat bars.
 *
 * Only `1h` and `1d` exist here — the old endpoint bucketed with `date_trunc`,
 * which has no vocabulary for 5m or 4h — so finer resolutions borrow the
 * nearest available series rather than returning nothing.
 */
async function fetchLegacyVwap(
	id: string,
	interval: string,
	from: number,
	to: number
): Promise<OhlcvBar[] | null> {
	const legacy =
		interval === '1w' || interval === '1d' || interval === '12h' || interval === '4h' ? '1d' : '1h'
	// Clamp rather than let the server reject the whole request. Slightly
	// inside the cap so bucket alignment on the server cannot push it back over.
	const max = LEGACY_MAX_WINDOW[legacy] - 86400
	const start = Math.max(from, to - max)
	const url = `${CHART_BASE}/collection/${encodeURIComponent(
		id
	)}/chart?interval=${legacy}&start=${start}&end=${to}`
	const res = await fetch(url)
	if (!res.ok) {
		return null
	}
	const json = await res.json()
	// The endpoint answers errors with a 200 and an `{error: true}` body, so a
	// non-array payload is a failure, not an empty series.
	const points = Array.isArray(json) ? json : json?.points
	if (!Array.isArray(points)) {
		return null
	}
	// vwap arrives in BTC; the rest of the feed works in satoshis.
	return points
		.filter((p: { time?: number; vwap?: number }) => p?.time && p?.vwap)
		.map((p: { time: number; vwap: number }) => {
			const px = p.vwap * 1e8
			return {
				time: p.time,
				open: px,
				high: px,
				low: px,
				close: px,
				median: px,
				volume: 0,
				trades: 0,
				synthetic: true
			}
		})
}

/**
 * A market symbol's candles, straight from Hyperliquid.
 *
 * `/markets` already priced itself this way; routing it through the shared
 * datafeed changes where the request is made, not what it returns. Hyperliquid
 * serves real OHLC, so a market chart is never in degraded mode.
 */
async function fetchMarket(
	coin: string,
	interval: string,
	from: number,
	to: number
): Promise<OhlcvBar[] | null> {
	const candles = await fetchCandleSnapshot(coin, interval, from * 1000, to * 1000)
	if (!Array.isArray(candles)) {
		return null
	}
	return candles.map((c) => ({
		// Hyperliquid stamps a candle with its OPEN time in ms.
		time: Math.floor(c.t / 1000),
		open: Number(c.o),
		high: Number(c.h),
		low: Number(c.l),
		close: Number(c.c),
		median: Number(c.c),
		volume: Number(c.v) || 0,
		trades: c.n ?? 0,
		synthetic: false
	}))
}

/**
 * Smallest `range` the alkanes chart endpoint offers that still covers a
 * window.
 *
 * That endpoint is range-based rather than start/end-based, and each range
 * carries its own resolution. Asking for a wider range than needed would
 * silently coarsen the candles, so the narrowest covering range is chosen and
 * the result is then clipped to what was actually asked for.
 */
function alkaneRangeFor(spanSecs: number): string {
	if (spanSecs <= 86_400) {
		return '1d'
	}
	if (spanSecs <= 7 * 86_400) {
		return '7d'
	}
	if (spanSecs <= 30 * 86_400) {
		return '30d'
	}
	return 'all'
}

/**
 * An alkane asset's USD candles.
 *
 * `currency: "frbtc"` with an empty series is a real answer — "this asset has
 * no USD coverage" — not a failure, and is reported as no data rather than as
 * an error so the chart says so plainly.
 */
async function fetchAlkane(id: string, from: number, to: number): Promise<OhlcvBar[] | null> {
	const range = alkaneRangeFor(Math.max(to - from, 0))
	const res = await fetch(
		`${CHART_BASE}/alkane/token/${encodeURIComponent(id)}/chart?range=${range}`
	)
	if (!res.ok) {
		return null
	}
	const json = await res.json()
	const candles = Array.isArray(json?.candles) ? json.candles : null
	if (!candles) {
		return null
	}
	return candles
		.filter((c: { ts: number }) => c.ts >= from && c.ts <= to)
		.map(
			(c: {
				ts: number
				open: number
				high: number
				low: number
				close: number
				volume: number
			}) => ({
				time: c.ts,
				open: c.open,
				high: c.high,
				low: c.low,
				close: c.close,
				median: c.close,
				volume: c.volume ?? 0,
				trades: 0,
				synthetic: false
			})
		)
}

/**
 * Dispatch to whichever backend owns this symbol's price history.
 *
 * Only collections have a degraded path: markets and alkanes already serve
 * real OHLC, so `degraded` is meaningful for collections alone.
 */
/**
 * The last close seen for an asset in a denomination, kept across visits so
 * the price scale is right before the first bar arrives. A convenience, not
 * state: a miss costs one small request.
 */
const TYPICAL_KEY = 'owChart.typical'

function readTypical(id: string, denom: Denom): number | undefined {
	try {
		const raw = window.localStorage.getItem(`${TYPICAL_KEY}:${id}:${denom}`)
		const v = raw === null ? NaN : Number(raw)
		return Number.isFinite(v) && v > 0 ? v : undefined
	} catch {
		return undefined
	}
}

function rememberTypical(id: string, denom: Denom, value: number) {
	try {
		window.localStorage.setItem(`${TYPICAL_KEY}:${id}:${denom}`, String(value))
	} catch {
		// Storage unavailable; the next resolve reads a bar instead.
	}
}

/** Where a market-cap request gets its supply from, if the server lacks one. */
interface SupplySource {
	hint?: number
	resolve?: () => Promise<number | undefined>
}

/**
 * Supplies resolved after a refusal, per asset. Every later request for the
 * asset carries the number instead of being refused and resolving it again
 * -- a history page or the open-bucket poll must not each pay a round trip.
 */
const resolvedSupply = new Map<string, number>()

function supplyFrom(options: DatafeedOptions, id: string): SupplySource {
	return {
		hint: options.supplyHint?.() ?? resolvedSupply.get(id),
		resolve: options.resolveSupplyHint
	}
}

async function fetchBars(
	kind: ChartKind,
	id: string,
	denom: Denom,
	interval: string,
	from: number,
	to: number,
	supply: SupplySource = {}
): Promise<{
	bars: OhlcvBar[] | null
	degraded: boolean
	unavailable?: string
	outliers?: ChartOutlier[]
	prints?: ChartOutlier[]
	trend?: ChartTrendPoint[]
	violations?: ChartViolation[]
}> {
	if (kind === 'market') {
		return { bars: await fetchMarket(id, interval, from, to), degraded: false }
	}
	if (kind === 'alkane') {
		return { bars: await fetchAlkane(id, from, to), degraded: false }
	}

	let primary = await fetchOhlcvDetailed(id, denom, interval, from, to, supply.hint)
	// Refused for want of a supply, and the page can find one: hand it over
	// and ask once more. Every later request carries it through `hint`.
	if (!primary.bars && denom === 'mcap' && supply.hint === undefined && supply.resolve) {
		const found = await supply.resolve().catch(() => undefined)
		if (found && Number.isFinite(found) && found > 0) {
			resolvedSupply.set(id, found)
			primary = await fetchOhlcvDetailed(id, denom, interval, from, to, found)
		}
	}
	if (primary.bars) {
		return {
			bars: primary.bars,
			degraded: false,
			outliers: primary.outliers,
			prints: primary.prints,
			trend: primary.trend,
			violations: primary.violations
		}
	}
	// A USD or market-cap series cannot be reconstructed from a BTC-denominated
	// line without inventing a bitcoin price history, so it is refused rather
	// than faked.
	if (denom !== 'sats') {
		return { bars: null, degraded: false, unavailable: primary.reason }
	}
	return { bars: await fetchLegacyVwap(id, interval, from, to), degraded: true }
}

/**
 * Resample caller-supplied bars onto the chart's own resolution.
 *
 * TradingView requires bar times to align with the resolution it asked for and
 * silently discards the ones that do not — which is how a page that hands over
 * a perfectly good series gets a chart reading `O ∅ H ∅ L ∅ C ∅`.
 *
 * Callers cannot reasonably guarantee that alignment. The alkanes feed, for
 * instance, picks its own bucket width from the range it was asked for (~20
 * minutes for a day, four hours for a month), and that width is not one of the
 * resolutions a user can pick in the toolbar. So rather than making every
 * caller pre-bucket its data, the feed buckets it here.
 *
 * Aggregation is the honest direction: open is the first bar's open, close the
 * last bar's close, high and low the extremes, volume the sum. Going the other
 * way — inventing finer bars than the source has — is not possible, and a
 * resolution finer than the source simply yields a sparser chart rather than
 * fabricated detail.
 */
function resampleBars(bars: OhlcvBar[], bucketSecs: number): OhlcvBar[] {
	if (bars.length === 0 || bucketSecs <= 0) {
		return bars
	}
	const byBucket = new Map<number, OhlcvBar>()
	// Sort first so "first" and "last" in a bucket mean what they say.
	for (const b of [...bars].sort((x, y) => x.time - y.time)) {
		const start = b.time - (b.time % bucketSecs)
		const acc = byBucket.get(start)
		if (!acc) {
			byBucket.set(start, { ...b, time: start })
			continue
		}
		acc.high = Math.max(acc.high, b.high)
		acc.low = Math.min(acc.low, b.low)
		acc.close = b.close
		acc.median = b.median
		acc.volume += b.volume || 0
		acc.trades += b.trades || 0
	}
	return [...byBucket.values()].sort((a, b) => a.time - b.time)
}

export function createDatafeed(options: DatafeedOptions = {}) {
	const degraded = new Set<string>()
	const subscriptions = new Map<string, ReturnType<typeof setInterval>>()

	return {
		onReady(callback: (config: unknown) => void) {
			// TradingView requires this to be asynchronous.
			setTimeout(
				() =>
					callback({
						supported_resolutions: SUPPORTED_RESOLUTIONS,
						supports_marks: Boolean(options.getMarks),
						supports_time: true,
						supports_timescale_marks: false
					}),
				0
			)
		},

		searchSymbols(
			_userInput: string,
			_exchange: string,
			_symbolType: string,
			onResult: (items: unknown[]) => void
		) {
			// Symbol search is driven by the site's own search UI, which knows
			// about collections; the chart's built-in search stays empty rather
			// than offering a second, worse one.
			onResult([])
		},

		async resolveSymbol(
			symbolName: string,
			onResolve: (info: unknown) => void,
			onError: (reason: string) => void
		) {
			const parsed = parseSymbol(symbolName)
			if (!parsed) {
				onError(`unknown symbol: ${symbolName}`)
				return
			}
			const { id, denom } = parsed
			// The price scale is fixed at resolve time and the axis cannot
			// place ticks finer than it, so it has to be sized to the series
			// now: a token at 0.00007953 sats needs a step of 1e-8, a market
			// cap needs a step of 1. The page's own number wins, then the
			// last visit's, then one bar from the server.
			let typical: number | undefined =
				parsed.kind === 'collection' ? options.typicalPrice?.(denom) ?? readTypical(id, denom) : undefined
			if (typical === undefined && parsed.kind === 'collection') {
				const now = Math.floor(Date.now() / 1000)
				const { bars } = await fetchBars(
					parsed.kind,
					id,
					denom,
					'1d',
					now - 7 * 86_400,
					now,
					supplyFrom(options, id)
				)
				const last = bars?.[bars.length - 1]
				if (last && Number.isFinite(last.close) && last.close > 0) {
					typical = last.close
					rememberTypical(id, denom, typical)
				}
			}
			setTimeout(
				() =>
					onResolve({
						name: symbolName,
						ticker: symbolName,
						description: options.displayName || id,
						type: 'crypto',
						session: '24x7',
						timezone: 'Etc/UTC',
						exchange: 'Ordinals Wallet',
						listed_exchange: 'Ordinals Wallet',
						format: 'price',
						minmov: 1,
						pricescale: typical !== undefined ? priceScaleForPrice(typical) : priceScaleFor(denom),
						has_intraday: true,
						has_daily: true,
						has_weekly_and_monthly: true,
						supported_resolutions: SUPPORTED_RESOLUTIONS,
						volume_precision: 0,
						data_status: 'streaming',
						// The unit shown on the price axis, so a user can never
						// mistake a satoshi chart for a dollar one.
						currency_code: denom === 'sats' ? 'sats' : denom === 'mcap' ? 'USD (cap)' : 'USD'
					}),
				0
			)
		},

		async getBars(
			symbolInfo: { name: string },
			resolution: string,
			periodParams: { from: number; to: number; firstDataRequest: boolean },
			onResult: (bars: Bar[], meta: { noData: boolean }) => void,
			onError: (reason: string) => void
		) {
			const parsed = parseSymbol(symbolInfo.name)
			const interval = resolutionToInterval(resolution)
			if (!parsed || !interval) {
				onError(`unsupported request: ${symbolInfo.name} @ ${resolution}`)
				return
			}

			// Caller-supplied series: no fetch, no degraded path, no widening —
			// the caller decides what exists.
			if (options.staticBars) {
				const all = resampleBars(options.staticBars(), resolutionSeconds(resolution))
				const within = all.filter((b) => b.time >= periodParams.from && b.time <= periodParams.to)
				if (within.length === 0) {
					onResult([], { noData: true })
					return
				}
				const bars = within.map(coerceBar).sort((a, b) => a.time - b.time)
				learnUnit(
					symbolInfo.name,
					within.map((c) => c.close)
				)
				options.onBars?.(symbolInfo.name, bars)
				onResult(bars, { noData: false })
				return
			}

			try {
				const { kind, id, denom } = parsed
				let {
					bars: candles,
					degraded: fellBack,
					unavailable,
					outliers,
					prints,
					trend,
					violations
				} = await fetchBars(
					kind,
					id,
					denom,
					interval,
					clampWindow(periodParams.from, periodParams.to, resolution),
					periodParams.to,
					supplyFrom(options, id)
				)

				chartLog('feed', 'history', {
					symbol: symbolInfo.name,
					resolution,
					bars: candles?.length ?? null,
					outliers: outliers?.length ?? null,
					trend: trend?.length ?? null,
					unavailable
				})
				{
					const last = candles?.[candles.length - 1]
					if (last && Number.isFinite(last.close) && last.close > 0) {
						rememberTypical(id, denom, last.close)
					}
				}
				if (fellBack && candles && !degraded.has(symbolInfo.name)) {
					degraded.add(symbolInfo.name)
					options.onDegraded?.(symbolInfo.name)
				}
				if (unavailable) {
					options.onDenomUnavailable?.(symbolInfo.name, unavailable)
				}
				if (outliers) {
					options.onOutliers?.(symbolInfo.name, outliers)
				}
				if (prints) {
					options.onPrints?.(symbolInfo.name, prints)
				}
				if (trend) {
					options.onTrend?.(symbolInfo.name, trend)
				}
				if (violations) {
					options.onInvariants?.(symbolInfo.name, violations)
				}

				// An asset that has not traded recently returns an empty window,
				// which TradingView renders as "no data" even though it has years
				// of history just out of view. The OHLCV endpoint handles this by
				// seeding from the last close before the window; the legacy
				// endpoint has no such seed, so widen the ask once and let the
				// chart land on the real history instead of an empty screen.
				// A refused denomination (no circulating supply for market cap)
				// is refused for every window. Widening the ask only makes the
				// server build a year of candles before refusing again -- 51s
				// measured on tap-DMT-NAT -- while the chart sits empty
				// waiting to fall back.
				if ((!candles || candles.length === 0) && periodParams.firstDataRequest && !unavailable) {
					const widened = await fetchBars(
						kind,
						id,
						denom,
						interval,
						clampWindow(periodParams.to - WIDEN_LOOKBACK_SECS, periodParams.to, resolution),
						periodParams.to
					)
					candles = widened.bars
					if (widened.degraded && candles && !degraded.has(symbolInfo.name)) {
						degraded.add(symbolInfo.name)
						options.onDegraded?.(symbolInfo.name)
					}
					// The widened window is the one that produced the bars now
					// on screen, so it owns the band too. Without this the
					// chart drew a year of candles beside a band covering the
					// empty window that triggered the widen.
					if (widened.outliers) {
						options.onOutliers?.(symbolInfo.name, widened.outliers)
					}
					if (widened.prints) {
						options.onPrints?.(symbolInfo.name, widened.prints)
					}
					if (widened.trend) {
						options.onTrend?.(symbolInfo.name, widened.trend)
					}
				}

				if (!candles || candles.length === 0) {
					onResult([], { noData: true })
					return
				}

				const bars = candles.map(coerceBar).sort((a, b) => a.time - b.time)
				// Decide sats-vs-BTC from the first page, before the library
				// formats a single label for it.
				learnUnit(
					symbolInfo.name,
					candles.map((c) => c.close)
				)
				options.onBars?.(symbolInfo.name, bars)
				onResult(bars, { noData: false })
			} catch (e) {
				onError(e instanceof Error ? e.message : 'failed to load bars')
			}
		},

		subscribeBars(
			symbolInfo: { name: string },
			resolution: string,
			onTick: (bar: Bar) => void,
			listenerGuid: string
		) {
			// Caller-supplied series updates by re-rendering with new bars, not
			// by polling — there is nothing for this feed to subscribe to.
			if (options.staticBars) {
				return
			}

			const parsed = parseSymbol(symbolInfo.name)
			const interval = resolutionToInterval(resolution)
			if (!parsed || !interval) {
				return
			}
			const bucket = resolutionSeconds(resolution)

			// Poll only the bucket currently in progress. The API never caches
			// the open bucket (closed ones are immutable and served from NVMe),
			// so this is the cheapest request that can move the price.
			const tick = async () => {
				try {
					const now = Math.floor(Date.now() / 1000)
					const openBucket = now - (now % bucket)
					const { bars: candles } = await fetchBars(
						parsed.kind,
						parsed.id,
						parsed.denom,
						interval,
						openBucket,
						now + bucket,
						supplyFrom(options, parsed.id)
					)
					const last = candles?.[candles.length - 1]
					if (last) {
						const bar = coerceBar(last)
						onTick(bar)
						options.onTick?.(symbolInfo.name, bar)
					}
				} catch {
					// A failed poll is not worth surfacing: the next one is
					// fifteen seconds away and the chart still holds the last
					// good bar.
				}
			}

			void tick()
			subscriptions.set(listenerGuid, setInterval(tick, REALTIME_POLL_MS))
		},

		unsubscribeBars(listenerGuid: string) {
			const handle = subscriptions.get(listenerGuid)
			if (handle) {
				clearInterval(handle)
				subscriptions.delete(listenerGuid)
			}
		},

		async getMarks(
			symbolInfo: { name: string },
			from: number,
			to: number,
			onData: (marks: ChartMark[]) => void
		) {
			if (!options.getMarks) {
				onData([])
				return
			}
			try {
				onData(await options.getMarks(symbolInfo.name, from, to))
			} catch {
				onData([])
			}
		}
	}
}
